import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Users, Package, ScanLine } from "lucide-react";

function Stat({ label, value, Icon }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-primary/60"/>}
      </div>
      <div className="text-3xl font-heading font-semibold mt-2">{value}</div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    verified: "bg-primary/10 text-primary border-primary/30",
    pending: "bg-secondary/10 text-secondary border-secondary/30",
    rejected: "bg-destructive/10 text-destructive border-destructive/30",
    active: "bg-primary/10 text-primary border-primary/30",
  };
  return <Badge variant="outline" className={`${map[s] || ""} capitalize`}>{s}</Badge>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviewBatch, setReviewBatch] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  const load = async () => {
    try {
      const [s, b, u] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/batches"), api.get("/admin/users"),
      ]);
      setStats(s.data); setBatches(b.data.items); setUsers(u.data.items);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/batches/${id}/status`, { status, note: reviewNote || null });
      toast.success(`Batch ${status}`);
      setReviewBatch(null); setReviewNote("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const setOnboarding = async (uid, status) => {
    try {
      await api.post(`/admin/users/${uid}/onboarding`, { status });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-secondary">Gudlink Admin</div>
          <h1 className="font-marketing text-4xl sm:text-5xl mt-2">Control room</h1>
          <p className="text-muted-foreground mt-1">Oversight of onboarding, batches, and trade activity.</p>
        </div>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total batches" value={stats.total_batches} Icon={Package}/>
          <Stat label="Verified" value={stats.verified} Icon={CheckCircle2}/>
          <Stat label="Pending" value={stats.pending} Icon={Clock}/>
          <Stat label="Total scans" value={stats.total_scans} Icon={ScanLine}/>
          <Stat label="Unique scanners" value={stats.unique_scanners} Icon={Users}/>
          <Stat label="FPOs" value={stats.fpos} Icon={Users}/>
          <Stat label="Buyers" value={stats.buyers} Icon={Users}/>
          <Stat label="Rejected" value={stats.rejected} Icon={XCircle}/>
        </div>
      )}

      <Tabs defaultValue="batches" className="mt-10">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="batches" className="mt-6">
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Batch</th>
                  <th className="text-left p-3">FPO</th>
                  <th className="text-left p-3">Product</th>
                  <th className="text-left p-3">Qty</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">Docs</th>
                  <th className="text-left p-3">Scans</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id} className="border-t border-border" data-testid={`admin-batch-row-${b.batch_code}`}>
                    <td className="p-3 font-medium">{b.batch_code}</td>
                    <td className="p-3">{b.fpo_org || b.fpo_name}</td>
                    <td className="p-3 capitalize">{b.product_type}</td>
                    <td className="p-3">{b.quantity_kg} kg</td>
                    <td className="p-3">{b.source_location}</td>
                    <td className="p-3">
                      <div className="flex gap-1 text-xs">
                        <span className={b.lab_report ? "text-primary" : "text-muted-foreground"}>LAB</span>
                        <span>/</span>
                        <span className={b.sop_report ? "text-primary" : "text-muted-foreground"}>SOP</span>
                      </div>
                    </td>
                    <td className="p-3">{b.scans_count}</td>
                    <td className="p-3"><StatusBadge s={b.status}/></td>
                    <td className="p-3">
                      <Dialog open={reviewBatch?.id === b.id} onOpenChange={(o)=>{ if(!o){setReviewBatch(null); setReviewNote("");} }}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={()=>{setReviewBatch(b); setReviewNote(b.admin_note || "");}} data-testid={`review-btn-${b.batch_code}`}>Review</Button>
                        </DialogTrigger>
                        <DialogContent data-testid="review-dialog">
                          <DialogHeader><DialogTitle>Review {b.batch_code}</DialogTitle></DialogHeader>
                          <div className="text-sm text-muted-foreground">FPO: {b.fpo_org} · {b.source_location}</div>
                          <div className="text-sm mt-2">Docs: {b.lab_report ? "Lab ✓" : "Lab ✗"} · {b.sop_report ? "SOP ✓" : "SOP ✗"}</div>
                          <Textarea value={reviewNote} onChange={e=>setReviewNote(e.target.value)} placeholder="Optional note visible to FPO" className="mt-3" data-testid="admin-note-input"/>
                          <DialogFooter className="mt-4 gap-2">
                            <Button variant="outline" onClick={()=>setStatus(b.id, "rejected")} data-testid="reject-btn"><XCircle className="h-4 w-4 mr-1"/>Reject</Button>
                            <Button onClick={()=>setStatus(b.id, "verified")} className="bg-primary hover:bg-primary/90" data-testid="verify-btn"><CheckCircle2 className="h-4 w-4 mr-1"/>Verify</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Org</th>
                  <th className="text-left p-3">Onboarding</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-border">
                    <td className="p-3 font-medium">{u.name}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3 capitalize">{u.role}</td>
                    <td className="p-3">{u.org_name || "—"}</td>
                    <td className="p-3"><StatusBadge s={u.onboarding_status || "active"}/></td>
                    <td className="p-3">
                      {u.role === "fpo" && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={()=>setOnboarding(u.id, "active")} data-testid={`activate-${u.email}`}>Activate</Button>
                          <Button size="sm" variant="outline" onClick={()=>setOnboarding(u.id, "rejected")} data-testid={`reject-user-${u.email}`}>Reject</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
